import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import {
  EMAIL_SIGNOL_SUPPORT,
  signolImagesRepositoryURL,
} from "@src/constants/constants";
import AppUtils from "@src/utils/appUtils";
import { authEndpoints } from "@src/services/endpoints";

import Modal from "@src/components/atoms/Modal";
import Button from "@src/components/atoms/Button";
import Checkbox from "@src/components/atoms/Checkbox";
import { ImageWithRetry, Loader } from "@src/components/atoms";

const SecurityQuestionsModal = ({
  username,
  usernameType,
  verificationData,
  onClose,
  onSuccess,
  isSubmitting,
}) => {
  const navigateTo = useNavigate();
  const [answers, setAnswers] = useState(
    verificationData.questionOptions.map(() => false),
  );
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);

  const isDirty = answers.some((answer) => answer === true);

  const verifyMutation = useMutation({
    mutationKey: ["VERIFY_SECURITY_QUESTION"],
    mutationFn: (data) => authEndpoints.verifyOperatorSecurityQuestion(data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      console.error("Security question verification error:", error);
      setError("Account locked");
      setLocked(true);
    },
  });

  const handleAnswerChange = (index) => {
    setAnswers(answers.map((answer, i) => (i === index ? !answer : false)));
  };

  const handleSubmit = () => {
    const request = {
      username,
      usernameType,
      signature: verificationData.signature,
      answerOptions: [...answers],
    };
    verifyMutation.mutate(request);
  };

  const renderError = () => {
    if (locked) {
      return (
        <div className="flex flex-col justify-center h-full">
          <ImageWithRetry
            className="p-1 w-24 mx-auto"
            src={`${signolImagesRepositoryURL}/security_lock.png`}
            alt=""
          />
          <div className="text-3xl lg:text-4xl text-primary-500 font-bold py-6">
            Account locked
          </div>
          <div className="px-6 text-sm">
            As the security question was answered incorrectly, your account
            access has now been locked. Please contact
            <a
              className="cursor-pointer text-primary-500 underline hover:text-primary-700 mx-1"
              href={`mailto:${EMAIL_SIGNOL_SUPPORT}?Subject=Support`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {EMAIL_SIGNOL_SUPPORT}
            </a>
            for further assistance.
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col justify-center h-full">
        <div className="text-3xl lg:text-4xl text-primary-500 font-bold py-6">
          {error}
        </div>
        <div className="px-6 text-sm">
          Please contact
          <a
            className="cursor-pointer text-primary-500 underline hover:text-primary-700 mx-1"
            href={`mailto:${EMAIL_SIGNOL_SUPPORT}?Subject=Support`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {EMAIL_SIGNOL_SUPPORT}
          </a>
          for further assistance.
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (verifyMutation.isPending || isSubmitting) {
      return (
        <div className="py-16">
          <Loader />
        </div>
      );
    }

    if (error) {
      return renderError();
    }

    return (
      <>
        <div className="text-3xl lg:text-4xl text-primary-500 font-bold py-2">
          Security Question
        </div>
        <div className="p-4">
          To proceed to password reset, please confirm where you were working on
          <span className="mx-1 font-bold capitalize">
            {verificationData.question}
          </span>
          :
        </div>
        <div className="flex justify-center py-2">
          <div className="mx-auto">
            {verificationData.questionOptions.map((option, idx) => (
              <div key={idx} className="px-2">
                <Checkbox
                  checked={answers[idx] === true}
                  label={option}
                  type="other"
                  id={`option-${idx}`}
                  onChange={() => handleAnswerChange(idx)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center items-center p-4">
          <Button
            onClick={handleSubmit}
            disabled={!isDirty || verifyMutation.isPending}
            loading={verifyMutation.isPending}
          >
            Confirm
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex flex-col items-center justify-center text-center py-8 h-screen">
        <div
          className="fixed inset-0 transition-opacity"
          style={{ backdropFilter: "blur(5px) contrast(.8)" }}
        >
          <div className="absolute inset-0 bg-primary-500 opacity-75"></div>
        </div>
        <div
          className="rounded-xl bg-white shadow-2xl overflow-hidden transform transition-all w-5/6 max-h-520 sm:max-w-520"
          style={{ minHeight: "500px", width: "520px", zIndex: 999 }}
          role="dialog"
          aria-modal="true"
        >
          <div className="px-8 py-10 md:px-12 w-full h-full">
            <div className="flex justify-end items-center">
              <div
                className="cursor-pointer"
                onClick={locked ? () => navigateTo("/") : onClose}
              >
                <span className="text-2xl font-bold text-primary-500">×</span>
              </div>
            </div>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export const securityQuestionsModalControls = {
  username: {
    propType: PropTypes.string.isRequired,
    description: "Username for verification",
  },
  usernameType: {
    propType: PropTypes.string.isRequired,
    description: "Type of username (Username/Email address)",
  },
  verificationData: {
    propType: PropTypes.object.isRequired,
    description: "Security question data with question, options, and signature",
  },
  onClose: {
    propType: PropTypes.func.isRequired,
    description: "Close modal callback",
  },
  onSuccess: {
    propType: PropTypes.func.isRequired,
    description: "Success callback when verification passes",
  },
  isSubmitting: {
    propType: PropTypes.bool,
    description: "Show loader while parent is processing after verification",
  },
};

SecurityQuestionsModal.propTypes = AppUtils.getPropTypeFromControls(
  securityQuestionsModalControls,
);

export default SecurityQuestionsModal;
